import React, { useEffect, useRef } from "react";
import { getCsvImport, isInFlight } from "../../api/migrationsApi";
import { POLL_INTERVAL_MS } from "./useMigrationJob";

/**
 * How many polls a follower makes before giving up and reporting the job settled anyway:
 * 150 polls at 2 s is five minutes, comfortably past the stale-job reaper, so a job that
 * never settles cannot keep a timer alive for ever.
 */
export const MAX_FOLLOW_POLLS = 150;

export interface FollowImportUntilSettledProps {
  /** The import job to keep checking on. */
  guid: string;
  /**
   * Called exactly once, when the job has left the in-flight states, when checking on it
   * failed, or when the poll cap was reached — so the caller can refresh what it shows and
   * stop rendering this follower.
   */
  onSettled: (guid: string) => void;
}

/**
 * Follows one CSV import the dialog has let go of (AKL-813).
 *
 * The import dialog's own poller stops with the dialog, and a closed dialog cannot be
 * reopened to the same job (there is no organisation-level import history yet). A job
 * the server was still applying would therefore finish unseen and leave the caller's list
 * stale. The caller renders one of these per such job — React's per-key lifecycle gives
 * every job its own poller, so following a second job never cancels the first (PR #761
 * review). Renders nothing.
 */
const FollowImportUntilSettled: React.FC<FollowImportUntilSettledProps> = ({
  guid,
  onSettled,
}) => {
  // The latest callback, so a caller re-rendering with a fresh closure (over its current
  // search query, say) is what runs when the job settles. Written from an effect rather
  // than during render.
  const onSettledRef = useRef(onSettled);
  useEffect(() => {
    onSettledRef.current = onSettled;
  });

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;
    let polls = 0;

    const tick = async () => {
      try {
        const job = await getCsvImport(guid);
        if (stopped) return;
        polls += 1;
        if (isInFlight(job.status) && polls < MAX_FOLLOW_POLLS) {
          timer = setTimeout(tick, POLL_INTERVAL_MS);
          return;
        }
      } catch (err) {
        if (stopped) return;
        console.error("Error checking on a closed CSV import:", err);
      }
      onSettledRef.current(guid);
    };

    timer = setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [guid]);

  return null;
};

export default FollowImportUntilSettled;
